/**
 * Pure scheduling math for proactive MS-backend token refresh.
 *
 * The Token/Refresh APIs return `expires_in` (seconds), which the app converts
 * to an absolute `tokenExpiresAtMs` epoch (see `resolveTokenExpiresAtMs`).
 * Refresh is scheduled `skewMs` BEFORE that instant so a request in flight at
 * the boundary never carries an already-expired token.
 *
 * Kept as a pure function (no timers, no store) so it is unit-testable; the
 * timer wiring lives in `agent.store.ts`.
 */

/** Refresh this long before the token's actual expiry. */
export const PROACTIVE_REFRESH_SKEW_MS = 60_000

/**
 * Never schedule closer than this — protects against a stale/past
 * `tokenExpiresAtMs` (e.g. rehydrated from an old session) causing a
 * zero-delay hot loop.
 */
export const PROACTIVE_REFRESH_MIN_DELAY_MS = 5_000

/**
 * Milliseconds until the proactive refresh should fire, or `null` when there
 * is nothing valid to schedule against.
 */
export function computeProactiveRefreshDelayMs(
  tokenExpiresAtMs: number | null | undefined,
  nowMs: number,
  skewMs: number = PROACTIVE_REFRESH_SKEW_MS,
  minDelayMs: number = PROACTIVE_REFRESH_MIN_DELAY_MS
): number | null {
  if (
    typeof tokenExpiresAtMs !== 'number' ||
    !Number.isFinite(tokenExpiresAtMs) ||
    tokenExpiresAtMs <= 0
  ) {
    return null
  }
  return Math.max(tokenExpiresAtMs - nowMs - skewMs, minDelayMs)
}
